
/* global gtag */
/**
 * Google Analytics Service
 * Provides methods for tracking events with Google Analytics using gtag
 */
class GoogleAnalyticsService {
  constructor() {
    this.isInitialized = false;
    this.measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
  }

  /**
   * Load gtag script and initialize Google Analytics
   */
  init() {
    if (!this.measurementId || this.measurementId === 'G-XXXXXXXXXX') {
      console.warn('Google Analytics: Measurement ID not configured');
      return;
    }

    if (this.isInitialized) {
      console.log('Google Analytics: Already initialized');
      return;
    }

    try {
      // Load gtag script if not already loaded
      if (!window.gtag) {
        // Create script tag for gtag
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
        document.head.appendChild(script);

        // Initialize gtag function
        window.dataLayer = window.dataLayer || [];
        window.gtag = function() {
          window.dataLayer.push(arguments);
        };
      }

      // Configure GA
      window.gtag('js', new Date());
      window.gtag('config', this.measurementId);
      
      this.isInitialized = true;
      console.log('Google Analytics: Initialized successfully', this.measurementId);
    } catch (error) {
      console.error('Google Analytics: Initialization failed', error);
    }
  }

  /**
   * Track page views
   * @param {string} path - Page path
   * @param {string} title - Page title
   */
  trackPageView(path, title) {
    if (!this.isInitialized || !window.gtag) {
      console.warn('Google Analytics: Not initialized');
      return;
    }

    try {
      window.gtag('config', this.measurementId, {
        page_path: path,
        page_title: title
      });
      console.log('Google Analytics: Page view tracked', { path, title });
    } catch (error) {
      console.error('Google Analytics: Page view tracking failed', error);
    }
  }

  /**
  /**
   * Track custom events
   * @param {string} action - Event action
   * @param {Object} parameters - Event parameters
   */
  trackEvent(action, parameters = {}) {
    if (!this.isInitialized || !window.gtag) {
      console.warn('Google Analytics: Not initialized');
      return;
    }

    try {
      window.gtag('event', action, parameters);
      console.log('Google Analytics: Event tracked', { action, parameters });
    } catch (error) {
      console.error('Google Analytics: Event tracking failed', error);
    }
  }

  /**
   * Track user registration
   * @param {Object} data - Registration data
   */
  trackRegistration(data = {}) {
    const eventData = {
      event_category: 'engagement',
      event_label: data.method || 'email',
      value: 1,
      ...data
    };

    this.trackEvent('sign_up', eventData);
  }

  /**
   * Track user login
   * @param {Object} data - Login data
   */
  trackLogin(data = {}) {
    const eventData = {
      event_category: 'engagement',
      event_label: data.method || 'email',
      value: 1,
      ...data
    };

    this.trackEvent('login', eventData);
  }

  /**
   * Track website analysis start
   * @param {Object} data - Analysis data
   */
  trackWebsiteAnalysis(data = {}) {
    const eventData = {
      event_category: 'engagement',
      event_label: 'website_analysis',
      value: 1,
      custom_parameters: {
        analysis_type: data.type || 'pagespeed',
        url: data.url || ''
      },
      ...data
    };

    this.trackEvent('generate_lead', eventData);
  }

  /**
   * Track website addition
   * @param {Object} data - Website data
   */
  trackWebsiteAdded(data = {}) {
    const eventData = {
      event_category: 'engagement',
      event_label: 'website_added',
      value: 1,
      custom_parameters: {
        platform: data.platform || '',
        source: data.source || 'dashboard'
      },
      ...data
    };

    this.trackEvent('generate_lead', eventData);
  }

  /**
   * Track support ticket creation
   * @param {Object} data - Ticket data
   */
  trackSupportTicket(data = {}) {
    const eventData = {
      event_category: 'engagement',
      event_label: 'support_ticket',
      value: 1,
      custom_parameters: {
        ticket_type: data.type || 'general',
        source: data.source || 'dashboard'
      },
      ...data
    };

    this.trackEvent('generate_lead', eventData);
  }

  /**
   * Track purchases
   * @param {number} value - Purchase value
   * @param {string} currency - Currency code
   * @param {Object} data - Additional purchase data
   */
  trackPurchase(value, currency = 'USD', data = {}) {
    const eventData = {
      transaction_id: data.transaction_id || Date.now().toString(),
      value: parseFloat(value) || 0,
      currency: currency,
      event_category: 'ecommerce',
      items: data.items || [{
        item_id: data.item_id || 'purchase',
        item_name: data.item_name || 'Purchase',
        category: data.category || 'subscription',
        quantity: 1,
        price: parseFloat(value) || 0
      }],
      ...data
    };

    this.trackEvent('purchase', eventData);
  }

  /**
   * Track subscription events
   * @param {Object} data - Subscription data
   */
  trackSubscription(data = {}) {
    const eventData = {
      event_category: 'ecommerce',
      event_label: data.plan_name || 'subscription',
      value: data.value || 0,
      currency: data.currency || 'USD',
      custom_parameters: {
        subscription_tier: data.plan_name || '',
        billing_cycle: data.billing_cycle || 'monthly'
      },
      ...data
    };

    this.trackEvent('subscribe', eventData);
  }

  /**
   * Set user properties
   * @param {Object} properties - User properties
   */
  setUserProperties(properties = {}) {
    if (!this.isInitialized) {
      console.warn('Google Analytics: Not initialized');
      return;
    }

    try {
      gtag('config', this.measurementId, {
        custom_map: properties
      });
      console.log('Google Analytics: User properties set', properties);
    } catch (error) {
      console.error('Google Analytics: Setting user properties failed', error);
    }
  }

  /**
   * Track custom business events
   * @param {string} eventName - Custom event name
   * @param {Object} data - Event data
   */
  trackCustomEvent(eventName, data = {}) {
    const eventData = {
      event_category: 'custom',
      event_label: eventName,
      value: data.value || 1,
      ...data
    };

    this.trackEvent(eventName, eventData);
  }
}

// Create singleton instance
const googleAnalyticsService = new GoogleAnalyticsService();

export default googleAnalyticsService;
